import { useState, useEffect, useCallback } from "react";
import { apiService } from "../services/apiService";
import { getAccessToken } from "../services/authService";

const useDropdownValues = (enabled = true) => {
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [billingAccountProviderMap, setBillingAccountProviderMap] = useState(new Map());
  const [filterCombinations, setFilterCombinations] = useState([]);
  const [billingAccountsByProvider, setBillingAccountsByProvider] = useState([]);
  const [categoriesByProvider, setCategoriesByProvider] = useState([]);
  const [serviceTypesByProvider, setServiceTypesByProvider] = useState([]);
  const [inventoryFilterProviders, setInventoryFilterProviders] = useState([]);
  const [inventoryFilterBillingAccounts, setInventoryFilterBillingAccounts] = useState([]);
  const [inventoryFilterBillingAccountProviderMap, setInventoryFilterBillingAccountProviderMap] =
    useState(new Map());
  const [inventoryFilterBillingAccountsByProvider, setInventoryFilterBillingAccountsByProvider] =
    useState([]);
  const [billingProviders, setBillingProviders] = useState([]);
  const [activeBillingAccountsByProvider, setActiveBillingAccountsByProvider] = useState([]);
  const [glCodes, setGlCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasAuthError, setHasAuthError] = useState(false);

  const resetDropdownValues = useCallback(() => {
    setProviders([]);
    setCategories([]);
    setStatuses([]);
    setServiceTypes([]);
    setBillingAccounts([]);
    setBillingAccountProviderMap(new Map());
    setFilterCombinations([]);
    setBillingAccountsByProvider([]);
    setCategoriesByProvider([]);
    setServiceTypesByProvider([]);
    setInventoryFilterProviders([]);
    setInventoryFilterBillingAccounts([]);
    setInventoryFilterBillingAccountProviderMap(new Map());
    setInventoryFilterBillingAccountsByProvider([]);
    setBillingProviders([]);
    setActiveBillingAccountsByProvider([]);
    setGlCodes([]);
  }, []);

  const deduplicateArray = useCallback((array) => {
    const seen = new Map();
    array.forEach((item) => {
      const lowerItem = String(item).toLowerCase();
      if (!seen.has(lowerItem) || (item && item[0] === item[0].toUpperCase())) {
        seen.set(lowerItem, item);
      }
    });
    return Array.from(seen.values());
  }, []);

  const processBillingAccounts = useCallback((billingAccountsRaw) => {
    const billingAccounts = billingAccountsRaw.map((item) => {
      if (typeof item === "string") return item;
      if (item?.billing_account) return item.billing_account;
      return item;
    }).sort();

    const billingAccountProviderMap = new Map();
    billingAccountsRaw.forEach((item) => {
      if (item?.billing_account && item?.provider) {
        const account = String(item.billing_account).trim();
        const provider = String(item.provider).trim();
        if (account && provider) {
          const normalized = account.replace(/\s+/g, "").replace(/-/g, "");
          billingAccountProviderMap.set(normalized, provider);
          if (account !== normalized) {
            billingAccountProviderMap.set(account, provider);
          }
        }
      }
    });

    return { billingAccounts, billingAccountProviderMap };
  }, []);

  const fetchDropdownValues = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    if (hasAuthError) {
      console.log("⛔ Skipping dropdown values fetch due to authentication error");
      resetDropdownValues();
      setLoading(false);
      return;
    }

    const token = getAccessToken();
    if (!token) {
      resetDropdownValues();
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDropdownValues();

      if (response.success && response.data) {
        const providers = (response.data.providers || []).sort();
        const categories = (response.data.categories || []).sort();
        const statuses = deduplicateArray(response.data.statuses || []).sort();
        const serviceTypes = deduplicateArray(response.data.serviceTypes || []).sort();
        const { billingAccounts, billingAccountProviderMap } = processBillingAccounts(
          response.data.billingAccounts || []
        );

        const combinations = response.data.filterCombinations || [];
        const byProvider = response.data.billingAccountsByProvider || [];
        const catsByProvider = response.data.categoriesByProvider || [];
        const stByProvider = response.data.serviceTypesByProvider || [];

        const invProvRaw =
          response.data.inventoryFilterProviders !== undefined
            ? response.data.inventoryFilterProviders
            : providers;
        const invBillRaw =
          response.data.inventoryFilterBillingAccounts !== undefined
            ? response.data.inventoryFilterBillingAccounts
            : response.data.billingAccounts || [];
        const invByProvRaw =
          response.data.inventoryFilterBillingAccountsByProvider !== undefined
            ? response.data.inventoryFilterBillingAccountsByProvider
            : response.data.billingAccountsByProvider || [];

        const invFiltered = processBillingAccounts(invBillRaw);

        setProviders(providers);
        setCategories(categories);
        setStatuses(statuses);
        setServiceTypes(serviceTypes);
        setBillingAccounts(billingAccounts);
        setBillingAccountProviderMap(billingAccountProviderMap);
        setFilterCombinations(Array.isArray(combinations) ? combinations : []);
        setBillingAccountsByProvider(Array.isArray(byProvider) ? byProvider : []);
        setCategoriesByProvider(Array.isArray(catsByProvider) ? catsByProvider : []);
        setServiceTypesByProvider(Array.isArray(stByProvider) ? stByProvider : []);
        setInventoryFilterProviders(
          Array.isArray(invProvRaw) ? [...invProvRaw].sort() : []
        );
        setInventoryFilterBillingAccounts(invFiltered.billingAccounts);
        setInventoryFilterBillingAccountProviderMap(invFiltered.billingAccountProviderMap);
        setInventoryFilterBillingAccountsByProvider(
          Array.isArray(invByProvRaw) ? invByProvRaw : []
        );
        setBillingProviders(
          Array.isArray(response.data.billingProviders)
            ? [...response.data.billingProviders].sort()
            : []
        );
        setActiveBillingAccountsByProvider(
          Array.isArray(response.data.activeBillingAccountsByProvider)
            ? response.data.activeBillingAccountsByProvider
            : []
        );
        setGlCodes(Array.isArray(response.data.glCodes) ? response.data.glCodes : []);
      } else {
        console.warn("Dropdown values API response was not successful, using empty arrays");
        resetDropdownValues();
      }
    } catch (err) {
      console.error("Error fetching dropdown values:", err);
      setError(err);

      if (err.status === 401 || err.isAuthError) {
        console.log("Authentication error detected in dropdown values - stopping requests");
        setHasAuthError(true);
      }

      resetDropdownValues();
    } finally {
      setLoading(false);
    }
  }, [enabled, hasAuthError, resetDropdownValues, deduplicateArray, processBillingAccounts]);

  useEffect(() => {
    if (enabled) {
      fetchDropdownValues();
    }
  }, [fetchDropdownValues, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchDropdownValues();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchDropdownValues, enabled]);

  return {
    providers,
    categories,
    statuses,
    serviceTypes,
    billingAccounts,
    billingAccountProviderMap,
    filterCombinations,
    billingAccountsByProvider,
    categoriesByProvider,
    serviceTypesByProvider,
    inventoryFilterProviders,
    inventoryFilterBillingAccounts,
    inventoryFilterBillingAccountProviderMap,
    inventoryFilterBillingAccountsByProvider,
    billingProviders,
    activeBillingAccountsByProvider,
    glCodes,
    loading,
    error,
    refresh: fetchDropdownValues,
  };
};

export default useDropdownValues;

